import {
	SPECIAL_EVENT_TEMPLATE_ID,
	type SkuId,
	storesWhichSupportRecommendationsCalculation,
	type StoreName
} from '@bluebrick/discounts-service-module'
import { groupBy } from 'lodash-es'
import { DateTime } from 'luxon'
import { defineStore, storeToRefs } from 'pinia'
import { computed, nextTick, ref } from 'vue'
import { now } from '@/common/dateUtils'
import {
	decodeNotes,
	getAnonymizedEventName,
	haveSameDates,
	haveSameName,
	isCooldownEvent,
	isCustomerCreatedEvent,
	isDiscountEvent,
	isNil,
	isOpportunityEvent,
	isStoreOpportunityEvent,
	normalizeSKUNames,
	sortByProductNameComparator,
	transactionSpanWrapper
} from '@/common/utils'
import { useActionsStore } from '@/features/actions/useActionsStore'
import { useDataHealthStore } from '@/features/dataHealth/useDataHealthStore'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { useCalendarStore } from '@/features/slicers/useCalendarStore'
import { fetchEventsWithCooldowns, fetchOpportunities, fetchRecommendations } from '@/features/stores/storesRemoteRepository'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { useFeatureFlagStore } from '@/features/user/useFeatureFlagStore'
import { type EventData, type SkuData, type StoreData } from '@/types'

type StoreLoadingState = 'idle' | 'loadingEventsWithCooldowns' | 'loadingOpportunities' | 'loadingRecommendations'

const useEventsStoreInner = defineStore('eventsInner', () => {
	const storeDataLoadingState = ref<Map<StoreName, StoreLoadingState>>(new Map())
	const skuDataLoadingState = ref<Map<SkuId, StoreLoadingState>>(new Map())

	return {
		storeDataLoadingState,
		skuDataLoadingState
	}
})

export const useEventsStore = defineStore('events', () => {
	const innerStore = useEventsStoreInner()
	const calendarStore = useCalendarStore()
	const { favouriteSKUIdsByStore, favouriteSKUIds, storesBySKUId } = storeToRefs(useSkusStore())
	const { startTransaction, trackEvent } = useTelemetry()
	const { storeContainerExpandedState, stores } = storeToRefs(useStoresStore())
	const { shouldAnonymizeData } = storeToRefs(useFeatureFlagStore())
	const actionsStore = useActionsStore()
	const { lastUpdateDatePerStore } = storeToRefs(useDataHealthStore())

	const storeEvents = ref<Map<StoreName, StoreData>>(new Map())

	const selectedSKUIds = ref(new Set<SkuId>())
	const selectedSKUIdsForStore = computed<(store: StoreName | undefined) => SkuId[]>(
		() => (store: StoreName | undefined) => (store ? [...selectedSKUIds.value].filter((skuId) => store === storesBySKUId.value.get(skuId)) : [])
	)

	const storeSKUs = computed(() => (store: StoreName | undefined) => (store ? (storeEvents.value.get(store)?.skus ?? []) : []))

	const storeSKUsByProductName = computed<(store: StoreName | undefined) => Record<string, SkuData[]>>(
		() => (store: StoreName | undefined) => (store ? groupBy(storeEvents.value.get(store)?.skus ?? [], 'product_name') : {})
	)

	const storeDataLoadingState = computed(() => (store: StoreName) => innerStore.storeDataLoadingState.get(store))
	const skuDataLoadingState = computed(() => (skuId: SkuId) => innerStore.skuDataLoadingState.get(skuId))
	const isLoadingStoreData = computed(
		() => (store: StoreName) => !!innerStore.storeDataLoadingState.get(store) && innerStore.storeDataLoadingState.get(store) !== 'idle'
	)
	const isLoadingSKUData = computed(
		() => (skuId: SkuId) => !!innerStore.skuDataLoadingState.get(skuId) && innerStore.skuDataLoadingState.get(skuId) !== 'idle'
	)
	const isLoadingEventsWithCooldowns = computed(
		() => (store: StoreName) => innerStore.storeDataLoadingState.get(store) === 'loadingEventsWithCooldowns'
	)

	const dateRangeBeingCurrentlyLoaded = ref(new Map<StoreName | SkuId, string>())
	const dateLoadingAbortControllers = ref(new Map<StoreName | SkuId, AbortController>())

	async function loadStoresData(storeNames: StoreName[]) {
		// Enable the loading state on actions button/panel until events and actions are reloaded
		actionsStore.indicatePendingRefresh()

		await Promise.allSettled(storeNames.map((storeName) => loadStoreData(storeName, true, true)))

		// Update actions once any store(s) data is updated
		// In theory this could be optimized to update the actions with per-store granularity,
		// but for now we'll settle with single global update and then we'll see if this needs
		// any improvements
		actionsStore.fetch()
	}

	async function loadAllStoresData() {
		await Promise.allSettled(stores.value.map(({ id }) => loadStoreData(id, true, true)))
	}

	async function loadStoreData(storeName: StoreName, forceReload?: boolean, evenIfCollapsed?: boolean) {
		trackEvent('app', 'Start loading events for store.', { storeName })

		if (!storeContainerExpandedState.value.get(storeName) && !evenIfCollapsed) {
			// No need to load data for store which container is collapsed. Data will be loaded
			// anyway when contained is expanded
			trackEvent('app', 'Skipping loading of data for collapsed store.', { storeName })

			return
		}

		if (isLoadingStoreData.value(storeName)) {
			if (!forceReload && calendarStore.currentDateRangeHash === dateRangeBeingCurrentlyLoaded.value.get(storeName)) {
				// Selected range is currently loading, so nothing to do here
				return
			}

			// Another range is loading - cancel the requests and load the new range
			dateLoadingAbortControllers.value.get(storeName)?.abort()
			dateLoadingAbortControllers.value.delete(storeName)
		} else if (!forceReload && calendarStore.currentDateRangeHash === dateRangeBeingCurrentlyLoaded.value.get(storeName)) {
			// Selected range is already loaded, so nothing to do here
			return
		}

		dateRangeBeingCurrentlyLoaded.value.set(storeName, calendarStore.currentDateRangeHash)

		dateLoadingAbortControllers.value.set(storeName, new AbortController())

		const transaction = startTransaction('loadStoreData', 'loading events')

		await transactionSpanWrapper(() => loadEventsWithCooldowns(storeName), 'loadEventsWithCooldowns', transaction)

		trackEvent('app', 'Loaded events with cooldowns for store.', { storeName })

		if (storesWhichSupportRecommendationsCalculation.includes(storeName)) {
			await nextTick()

			await transactionSpanWrapper(() => loadOpportunities(storeName), 'loadOpportunities', transaction)
			trackEvent('app', 'Loaded opportunities for store.', { storeName })

			await nextTick()

			await transactionSpanWrapper(() => loadRecommendations(storeName), 'loadRecommendations', transaction)
			trackEvent('app', 'Loaded all events for store.', { storeName })
		}

		transaction?.end()
	}

	async function loadSKUData(storeName: StoreName, skuId: SkuId, forceReload?: boolean) {
		trackEvent('app', 'Start loading events for sku.', { storeName, skuId })

		if (isLoadingStoreData.value(storeName)) {
			// If there is already an ongoing reload of data for entire store then do not update this single SKU
			return
		}

		if (isLoadingSKUData.value(skuId)) {
			if (!forceReload && calendarStore.currentDateRangeHash === dateRangeBeingCurrentlyLoaded.value.get(skuId)) {
				// Selected range is currently loading, so nothing to do here
				return
			}

			// Another range is loading - cancel the requests and load the new range
			dateLoadingAbortControllers.value.get(skuId)?.abort()
			dateLoadingAbortControllers.value.delete(skuId)
		} else if (!forceReload && calendarStore.currentDateRangeHash === dateRangeBeingCurrentlyLoaded.value.get(skuId)) {
			// Selected range is already loaded, so nothing to do here
			return
		}

		dateRangeBeingCurrentlyLoaded.value.set(skuId, calendarStore.currentDateRangeHash)

		dateLoadingAbortControllers.value.set(skuId, new AbortController())

		await loadEventsWithCooldowns(storeName, skuId)
		trackEvent('app', 'Loaded events with cooldowns for sku.', { storeName, skuId })

		if (storesWhichSupportRecommendationsCalculation.includes(storeName)) {
			await nextTick()

			await loadOpportunities(storeName, skuId)
			trackEvent('app', 'Loaded opportunities for sku.', { storeName, skuId })

			await nextTick()

			await loadRecommendations(storeName, skuId)
			trackEvent('app', 'Loaded all events for sku.', { storeName, skuId })
		}
	}

	async function loadEventsWithCooldowns(storeName: StoreName, skuId?: SkuId) {
		try {
			setLoadingState('loadingEventsWithCooldowns', storeName, skuId)

			// Extend the loaded period a bit to make sure that events which are on
			// the edge of the original data range (from calendarStore) are also loaded
			const startDate = calendarStore.dataRangeStartDate.minus({ months: 1 })
			const endDate = calendarStore.dataRangeEndDate.plus({ months: 1 })
			const hasAnyFavouriteSKUs = favouriteSKUIds.value.length > 0
			// If no `skuId` argument is passed then use favourite SKUs as the list
			const skuIds = skuId ? [skuId] : (favouriteSKUIdsByStore.value.get(storeName) ?? [])

			if (hasAnyFavouriteSKUs && skuIds.length === 0) {
				// If studio has any favourite SKUs for other store(s), but does not have favourite SKUs
				// for this particular store then we just ignore this store and clean its timeline
				storeEvents.value.delete(storeName)

				return
			}

			const storeData = await fetchEventsWithCooldowns(
				storeName,
				startDate,
				endDate,
				skuIds.length > 0 ? skuIds : undefined,
				skuId ? dateLoadingAbortControllers.value.get(skuId)?.signal : dateLoadingAbortControllers.value.get(storeName)?.signal
			)

			if (!skuId) {
				// If updating entire store
				if (!storeData || storeData.skus.length === 0) {
					// No data was returned - clean the timeline
					storeEvents.value.delete(storeName)

					return
				}

				const skus = normalizeSKUNames(
					storeData.skus,
					(sku) => {
						sku.events = sku.events?.map((event) => ({
							...event,
							notes: decodeNotes(event.notes, shouldAnonymizeData.value),
							name: shouldAnonymizeData.value ? getAnonymizedEventName(event.name) : event.name
						}))

						return sku
						// This sorting is necessary for products to be shown in correct order in the timeline
					},
					shouldAnonymizeData.value
				).sort(sortByProductNameComparator)

				storeEvents.value.set(storeName, {
					studio_id: storeData.studio_id,
					store_name: storeName,
					start_date: startDate,
					end_date: endDate,
					skus
				})

				lastUpdateDatePerStore.value.set(storeName, DateTime.utc())

				return
			}

			// If updating single SKU
			const skuToUpdate = storeEvents.value.get(storeName)?.skus?.find((sku) => sku.unique_sku_id === skuId)

			if (skuToUpdate) {
				// Clean the timeline for given SKU before populating it with updated events list
				skuToUpdate.events = []
			}

			if (!storeData || storeData.skus.length === 0) {
				return
			}

			if (skuToUpdate) {
				skuToUpdate.events = (storeData.skus.find((sku) => sku.unique_sku_id === skuToUpdate.unique_sku_id)?.events ?? []).map((event) => ({
					...event,
					notes: decodeNotes(event.notes)
				}))
			}
		} finally {
			setLoadingState('idle', storeName, skuId)
		}
	}

	async function loadOpportunities(storeName: StoreName, skuId?: SkuId) {
		const endDate = calendarStore.dataRangeEndDate.plus({ month: 1 })

		try {
			setLoadingState('loadingOpportunities', storeName, skuId)

			const events: EventData[] = []
			const cooldowns: EventData[] = []

			// Use only events/cooldowns "younger" than 2 months ago (no need to send older ones)
			storeEvents.value
				.get(storeName)
				?.skus?.filter((sku) => (!skuId || sku.unique_sku_id === skuId) && sku.unique_sku_id !== SPECIAL_EVENT_TEMPLATE_ID)
				?.forEach((sku) =>
					sku.events?.forEach((event) => {
						if (event.end_date < now().minus({ months: 2 })) {
							return
						}

						if (isDiscountEvent(event) || isStoreOpportunityEvent(event)) {
							events.push(event)
						} else if (isCooldownEvent(event)) {
							cooldowns.push(event)
						}
					})
				)

			// If no `skuId` argument is passed then use favourite SKUs as the list.
			// If there are no favourite SKUs, send an empty list - This will result in calculating opportunities only for SKUs with already existing events.
			// Calculating opportunities for ALL skus (when there are no favourite) might be too heavy.
			const skuIds = skuId ? [skuId] : (favouriteSKUIdsByStore.value.get(storeName) ?? [])

			const opportunities = await fetchOpportunities(
				storeName,
				events,
				cooldowns,
				undefined, // Will use nearest start date with proper leadtime
				endDate.toJSDate(),
				skuIds,
				skuId ? dateLoadingAbortControllers.value.get(skuId)?.signal : dateLoadingAbortControllers.value.get(storeName)?.signal
			)

			const existingStoreData = storeEvents.value.get(storeName)

			if (existingStoreData) {
				const opportunitiesBySKUId = groupBy(opportunities, 'unique_sku_id')

				storeEvents.value.set(storeName, {
					...existingStoreData,
					skus: existingStoreData.skus.map((sku) => {
						if (sku.unique_sku_id in opportunitiesBySKUId) {
							return {
								...sku,
								events: [...sku.events, ...opportunitiesBySKUId[sku.unique_sku_id]]
							}
						}

						return sku
					})
				})
			}
		} finally {
			setLoadingState('idle', storeName, skuId)
		}
	}

	async function loadRecommendations(storeName: StoreName, skuId?: SkuId) {
		try {
			setLoadingState('loadingRecommendations', storeName, skuId)

			const events: EventData[] = []

			// Use only events "younger" than 1 month ago (no need to send older ones)
			storeEvents.value
				.get(storeName)
				?.skus?.filter((sku) => !skuId || sku.unique_sku_id === skuId)
				?.forEach((sku) =>
					sku.events?.forEach((event) => {
						if (event.start_date > now().minus({ month: 1 }) && isDiscountEvent(event) && !isOpportunityEvent(event)) {
							events.push(event)
						}
					})
				)

			if (events.some(isCustomerCreatedEvent)) {
				// Do not request recommendations if no customer created events were found
				const recommendations = await fetchRecommendations(storeName, events, dateLoadingAbortControllers.value.get(storeName)?.signal)

				const storeData = storeEvents.value.get(storeName)

				if (storeData && recommendations && Object.keys(recommendations).length > 0) {
					// Add recommendations to correct events
					storeEvents.value.set(storeName, {
						...storeData,
						skus: storeData.skus?.map((sku) => ({
							...sku,
							events: sku.events?.map((event) => {
								if (event.id && event.id in recommendations && recommendations[event.id].is_recommendation_available) {
									return {
										...event,
										recommended_discount_depth: isNil(recommendations[event.id].depth) ? undefined : recommendations[event.id].depth,
										recommended_start_date: isNil(recommendations[event.id].start_date) ? undefined : recommendations[event.id].start_date,
										recommended_end_date: isNil(recommendations[event.id].end_date) ? undefined : recommendations[event.id].end_date,
										isRecommendationAvailable: true
									}
								}

								return event
							})
						}))
					})
				}
			}
		} finally {
			setLoadingState('idle', storeName, skuId)
		}
	}

	function setLoadingState(state: StoreLoadingState, storeName: StoreName, skuId?: SkuId) {
		if (skuId) {
			innerStore.skuDataLoadingState.set(skuId, state)
		} else {
			innerStore.storeDataLoadingState.set(storeName, state)
		}
	}

	function toggleSKUSelection(skuId: SkuId) {
		if (isSKUSelected(skuId)) {
			unselectSKU(skuId)
		} else {
			selectSKU(skuId)
		}
	}

	function selectSKU(skuId: SkuId) {
		if (isSKUSelected(skuId)) {
			return
		}

		selectedSKUIds.value.add(skuId)
	}

	function setSelectedSKUIds(skuIds: SkuId[]) {
		selectedSKUIds.value = new Set(skuIds)
	}

	function selectAllSKUs(store: StoreName) {
		selectedSKUIds.value = new Set([...selectedSKUIds.value, ...storeSKUs.value(store).map(({ unique_sku_id }) => unique_sku_id)])
	}

	function unselectSKU(skuId: string) {
		selectedSKUIds.value.delete(skuId)
	}

	function unselectAllSKUs() {
		selectedSKUIds.value = new Set()
	}

	function isSKUSelected(skuId: SkuId) {
		return selectedSKUIds.value.has(skuId)
	}

	function isProductSelected(store: StoreName, productName: string) {
		if (selectedSKUIdsForStore.value(store).length === 0) {
			return false
		}

		return storeSKUsByProductName.value(store)[productName]?.every((sku) => isSKUSelected(sku.unique_sku_id))
	}

	function isProductPartiallySelected(store: StoreName, productName: string) {
		if (selectedSKUIdsForStore.value(store).length === 0) {
			return false
		}

		return (
			!storeSKUsByProductName.value(store)[productName]?.every((sku) => isSKUSelected(sku.unique_sku_id)) &&
			storeSKUsByProductName.value(store)[productName]?.some((sku) => isSKUSelected(sku.unique_sku_id))
		)
	}

	function selectAllRelatedSkus(event: EventData, store: StoreName | undefined) {
		unselectAllSKUs()

		storeSKUs.value(store).forEach(({ events, unique_sku_id }) => {
			const matchingEvent = events.find(
				(eventCandidate) => haveSameDates(event, eventCandidate) && haveSameName(event, eventCandidate) && isCustomerCreatedEvent(eventCandidate)
			)

			if (matchingEvent) {
				selectSKU(unique_sku_id)
			}
		})
	}

	function getMatchingEventForSkuId(event: EventData, skuID: string, store: StoreName | undefined): EventData | undefined {
		const sku = storeSKUs.value(store).find(({ unique_sku_id }) => unique_sku_id === skuID)

		const eventMatch = sku?.events.find(
			(eventCandidate) => haveSameDates(event, eventCandidate) && haveSameName(event, eventCandidate) && isCustomerCreatedEvent(eventCandidate)
		)

		return eventMatch
	}

	return {
		loadAllStoresData,
		loadStoresData,
		loadStoreData,
		loadSKUData,
		toggleSKUSelection,
		selectSKU,
		setSelectedSKUIds,
		selectAllSKUs,
		unselectSKU,
		unselectAllSKUs,
		isSKUSelected,
		isProductSelected,
		isProductPartiallySelected,
		selectAllRelatedSkus,
		getMatchingEventForSkuId,
		isLoadingStoreData,
		isLoadingSKUData,
		isLoadingEventsWithCooldowns,
		storeDataLoadingState,
		skuDataLoadingState,
		storeEvents,
		selectedSKUIdsForStore,
		storeSKUs
	}
})
